import React, { useState } from 'react';
import './PostflopStats.css';
import { useSelector } from 'react-redux';
import { selectGeneralStats } from '../../selectors';
import Card from '../../../../components/Card/Card';
import formatHands from '../../../../utils/formatHands';
import classNames from 'classnames';

import Text from '../../../../components/Text/Text';
import Flex from '../../../../components/Flex/Flex';
import type { PreflopOrMatrix, PreflopOrStatId, ThreeBetVsMatrix } from '@poker-stats/contracts';

const shouldShowPostflopSample = (sample: number | null | undefined): sample is number =>
  typeof sample === 'number' && sample < 99;

const PostflopStatValue = ({
  value,
  sample,
}: {
  value: number | null | string;
  sample?: number | null;
}) => {
  const hasSample = shouldShowPostflopSample(sample);

  return (
    <span className="Postflop__value-wrap">
      <span className="Postflop__value-body">
        <span className="Postflop__value-main">{value === null ? '-' : value}</span>
        {hasSample && <span className="Postflop__sample">{formatHands(sample)}</span>}
      </span>
    </span>
  );
};

export const PostflopStatsList = React.memo(
  ({
    name,
    values,
    sampleValues,
    compact,
    portable,
  }: {
    name: string;
    values: Array<number | null | string>;
    sampleValues?: Array<number | null>;
    compact?: boolean;
    portable?: boolean;
  }) => {
    const textSize = portable ? 2 : compact ? 2 : undefined;
    const gap = portable ? 3 : compact ? 4 : 8;

    return (
      <Flex
        vertical
        gap={gap}
        align="center"
        className={
          portable ? 'PortableStats__stat-col' : compact ? 'Postflop__stat-col' : undefined
        }
      >
        <Text type="primary" bold size={textSize}>
          {name}
        </Text>
        {values.map((x, index) => {
          const sample = !portable ? sampleValues?.[index] : undefined;
          return (
            <Flex key={index} vertical gap={1} align="center">
              <Text bold size={textSize}>
                <PostflopStatValue value={x} sample={sample} />
              </Text>
            </Flex>
          );
        })}
      </Flex>
    );
  },
);

const STREETS = ['FLP', 'TRN', 'RVR'] as const;

type StatColumn = {
  name: string;
  headerTitle?: string;
  portableName?: string;
  values: Array<number | null | string>;
  sampleValues?: Array<number | null>;
};

type PostflopView = 'main' | 'donk' | 'cbet' | 'bx' | 'preflop';

type CbetMode = 'cbet' | 'vscb';

const PREFLOP_OR_POS_KEYS = ['ep', 'mp', 'co', 'btn', 'sb', 'bb'] as const;

const PREFLOP_OR_COLUMN_KEYS = ['tot', ...PREFLOP_OR_POS_KEYS] as const;

const PREFLOP_OR_OPEN_RAISE_ROW = { key: 'openraise', label: 'R1' } as const;

const PREFLOP_OR_TOT_COLUMNS: Array<{ key: PreflopOrStatId; label: string }> = [
  PREFLOP_OR_OPEN_RAISE_ROW,
  { key: 'foldtothreebet', label: 'F3b' },
  { key: 'callthreebet', label: 'C3b' },
  { key: 'c3br', label: 'C3br' },
  { key: 'fourbet', label: '4bet' },
  { key: 'fourbr', label: '4br' },
  { key: 'foldtofivebet', label: 'F5b' },
  { key: 'isoraisev1', label: 'ISO1' },
  { key: 'isoraisev2', label: 'ISO2' },
  { key: 'isoraisev3', label: 'ISO3' },
  { key: 'limp', label: 'Limp' },
  { key: 'limpfold', label: 'L-F' },
  { key: 'limpraise', label: 'L-R' },
];

const PREFLOP_OR_TOT_OPEN_COLUMNS = PREFLOP_OR_TOT_COLUMNS.slice(0, 7);
const PREFLOP_OR_TOT_LIMP_COLUMNS = PREFLOP_OR_TOT_COLUMNS.slice(7);

const PREFLOP_OR_TOT_SECTIONS = [
  {
    title: 'Open raise lines',
    columns: PREFLOP_OR_TOT_OPEN_COLUMNS,
    gridClass: 'Postflop__grid--preflopor-tot-open',
  },
  {
    title: 'Iso & limp',
    columns: PREFLOP_OR_TOT_LIMP_COLUMNS,
    gridClass: 'Postflop__grid--preflopor-tot-limp',
  },
] as const;

const PREFLOP_OR_IP_COLUMNS: Array<{ key: PreflopOrStatId; label: string }> = [
  PREFLOP_OR_OPEN_RAISE_ROW,
  { key: 'foldtothreebetip', label: 'F3b' },
  { key: 'callthreebetip', label: 'C3b' },
  { key: 'c3brip', label: 'C3br' },
  { key: 'fourbetip', label: '4bet' },
  { key: 'fourbrip', label: '4br' },
  { key: 'foldtofivebetip', label: 'F5b' },
  { key: 'isoraisev1ip', label: 'ISO1' },
];

const PREFLOP_OR_OOP_COLUMNS: Array<{ key: PreflopOrStatId; label: string }> = [
  PREFLOP_OR_OPEN_RAISE_ROW,
  { key: 'foldtothreebetoop', label: 'F3b' },
  { key: 'callthreebetoop', label: 'C3b' },
  { key: 'c3broop', label: 'C3br' },
  { key: 'fourbetoop', label: '4bet' },
  { key: 'fourbroop', label: '4br' },
  { key: 'foldtofivebetoop', label: 'F5b' },
  { key: 'isoraisev1oop', label: 'ISO1' },
];

type PreflopOrGridVariant = 'tot' | 'ip' | 'oop';

const PREFLOP_OR_COLUMNS_BY_VARIANT: Record<
  PreflopOrGridVariant,
  Array<{ key: PreflopOrStatId; label: string }>
> = {
  tot: PREFLOP_OR_TOT_COLUMNS,
  ip: PREFLOP_OR_IP_COLUMNS,
  oop: PREFLOP_OR_OOP_COLUMNS,
};

const getPreflopOrColumnLabel = (key: (typeof PREFLOP_OR_COLUMN_KEYS)[number]) => {
  if (key === 'tot') {
    return 'TOT';
  }

  if (key === 'btn') {
    return 'BTN';
  }

  if (key === 'sb' || key === 'bb') {
    return key.toUpperCase();
  }

  return key.toUpperCase();
};

const PREFLOP_OR_DECIMAL_STAT_IDS = new Set<PreflopOrStatId>([
  'fourbr',
  'fourbrip',
  'fourbroop',
  'c3br',
  'c3brip',
  'c3broop',
]);

const formatPreflopOrValue = (value: number | null | undefined, statId: PreflopOrStatId) => {
  if (value === null || value === undefined) {
    return null;
  }

  if (PREFLOP_OR_DECIMAL_STAT_IDS.has(statId)) {
    return +value.toFixed(1);
  }

  return Math.round(value);
};

const PreflopOrMatrixGrid = ({
  matrix,
  variant = 'tot',
  embedded = false,
}: {
  matrix?: PreflopOrMatrix;
  variant?: PreflopOrGridVariant;
  embedded?: boolean;
}) => {
  const columns = PREFLOP_OR_COLUMNS_BY_VARIANT[variant];

  const renderGrid = (
    gridColumns: Array<{ key: PreflopOrStatId; label: string }>,
    gridClassName: string,
  ) => (
    <div
      className={classNames('Postflop__grid', 'Postflop__grid--preflopor', gridClassName, {
        'Postflop__grid--preflopor-ip': variant === 'ip',
        'Postflop__grid--preflopor-oop': variant === 'oop',
      })}
    >
      <div className="Postflop__cell Postflop__cell--corner" />
      {PREFLOP_OR_COLUMN_KEYS.map((posKey) => (
        <div
          key={posKey}
          className={classNames('Postflop__cell', 'Postflop__cell--header', {
            'Postflop__cell--tot-col': posKey === 'tot',
          })}
        >
          {getPreflopOrColumnLabel(posKey)}
        </div>
      ))}

      {gridColumns.map((stat) => (
        <React.Fragment key={stat.key}>
          <div className="Postflop__cell Postflop__cell--street">{stat.label}</div>
          {PREFLOP_OR_COLUMN_KEYS.map((posKey) => {
            const cell = matrix?.[posKey]?.[stat.key];

            return (
              <div
                key={`${stat.key}-${posKey}`}
                className={classNames('Postflop__cell', 'Postflop__cell--value', {
                  'Postflop__cell--tot-col': posKey === 'tot',
                })}
              >
                <PostflopStatValue
                  value={formatPreflopOrValue(cell?.value, stat.key)}
                  sample={cell?.sample}
                />
              </div>
            );
          })}
        </React.Fragment>
      ))}
    </div>
  );

  const content =
    variant === 'tot' ? (
      <div className="Postflop__preflopor-sections">
        {PREFLOP_OR_TOT_SECTIONS.map((section) => (
          <div key={section.title} className="Postflop__cbet-block Postflop__cbet-block--primary">
            <div className="Postflop__cbet-block-title">{section.title}</div>
            {renderGrid(section.columns, section.gridClass)}
          </div>
        ))}
      </div>
    ) : (
      renderGrid(columns, 'Postflop__grid--preflopor-single')
    );

  if (embedded) {
    return content;
  }

  return <div className="Postflop__table-wrap">{content}</div>;
};

const THREEBET_VS_HERO_KEYS = ['ep', 'mp', 'co', 'btn', 'sb', 'bb'] as const;

const THREEBET_VS_VILLAIN_COLUMNS = [
  { key: 'tot', label: 'TOT' },
  { key: 'ep', label: 'EP' },
  { key: 'mp', label: 'MP' },
  { key: 'co', label: 'CO' },
  { key: 'btn', label: 'BTN' },
  { key: 'sb', label: 'SB' },
] as const;

type ThreeBetVsMode = '3bet' | 'f4bet' | '5bet' | 'cc';

type PreflopMatrixMode = 'or' | 'pre-ip' | 'pre-oop' | 'sq1c' | ThreeBetVsMode;

const isPreflopOrMatrixMode = (mode: PreflopMatrixMode): mode is 'or' | 'pre-ip' | 'pre-oop' =>
  mode === 'or' || mode === 'pre-ip' || mode === 'pre-oop';

const getPreflopOrGridVariant = (mode: 'or' | 'pre-ip' | 'pre-oop'): PreflopOrGridVariant => {
  if (mode === 'pre-ip') {
    return 'ip';
  }

  if (mode === 'pre-oop') {
    return 'oop';
  }

  return 'tot';
};

const getThreeBetVsHeroLabel = (
  key: (typeof THREEBET_VS_HERO_KEYS)[number],
  mode: ThreeBetVsMode,
) => {
  const prefix =
    key === 'btn' ? 'BTN' : key === 'sb' ? 'SB' : key === 'bb' ? 'BB' : key.toUpperCase();

  return mode === 'cc' ? `${prefix} CC` : `${prefix} 3b`;
};

const formatThreeBetVsValue = (value: number | null | undefined) => {
  if (value === null || value === undefined) {
    return null;
  }

  return Number.isInteger(value) ? value : +value.toFixed(1);
};

const ThreeBetVsMatrixGrid = ({
  matrix,
  mode,
  embedded = false,
}: {
  matrix?: ThreeBetVsMatrix;
  mode: ThreeBetVsMode;
  embedded?: boolean;
}) => {
  const grid = (
    <div className="Postflop__grid Postflop__grid--threebetvs">
      <div className="Postflop__cell Postflop__cell--corner" />
      {THREEBET_VS_VILLAIN_COLUMNS.map((column) => (
        <div
          key={column.key}
          className={classNames('Postflop__cell', 'Postflop__cell--header', {
            'Postflop__cell--tot-col': column.key === 'tot',
          })}
        >
          {column.label}
        </div>
      ))}

      {THREEBET_VS_HERO_KEYS.map((heroKey) => {
        const row = matrix?.[heroKey];

        return (
          <React.Fragment key={heroKey}>
            <div className="Postflop__cell Postflop__cell--street">
              {getThreeBetVsHeroLabel(heroKey, mode)}
            </div>
            {THREEBET_VS_VILLAIN_COLUMNS.map((column) => {
              const cell = row?.[column.key];

              return (
                <div
                  key={`${heroKey}-${column.key}`}
                  className={classNames('Postflop__cell', 'Postflop__cell--value', {
                    'Postflop__cell--tot-col': column.key === 'tot',
                  })}
                >
                  <PostflopStatValue
                    value={formatThreeBetVsValue(cell?.value)}
                    sample={cell?.sample}
                  />
                </div>
              );
            })}
          </React.Fragment>
        );
      })}
    </div>
  );

  if (embedded) {
    return grid;
  }

  return <div className="Postflop__table-wrap">{grid}</div>;
};

const pickStatColumns = (columns: StatColumn[], names: string[]) =>
  names.flatMap((name) => {
    const column = columns.find((entry) => entry.name === name);
    return column ? [column] : [];
  });

type StatSectionBlockConfig = {
  title: string;
  columns: StatColumn[];
  streets?: readonly (typeof STREETS)[number][];
  mutedColumnNames?: string[];
  layout?: 'primary' | 'side';
  display?: 'grid' | 'inline-row';
};

const getColumnStreet = (column: StatColumn) => {
  const streetIndex = column.values.findIndex((value) => value !== null);

  if (streetIndex === -1) {
    return null;
  }

  return STREETS[streetIndex];
};

const StatInlineRowBlock = ({
  title,
  columns,
  portable,
}: {
  title: string;
  columns: StatColumn[];
  portable: boolean;
}) => (
  <div className="Postflop__cbet-block Postflop__cbet-block--side Postflop__cbet-block--inline-row">
    <div className="Postflop__cbet-block-title">{title}</div>
    <div className="Postflop__cbet-inline-row">
      {columns.map((column) => {
        const street = getColumnStreet(column);
        const streetIndex = street ? STREETS.indexOf(street) : -1;
        const value = streetIndex >= 0 ? column.values[streetIndex] : null;
        const sample = streetIndex >= 0 ? column.sampleValues?.[streetIndex] : undefined;
        const label = portable && column.portableName ? column.portableName : column.name;

        return (
          <div key={column.name} className="Postflop__cbet-inline-cell">
            <div
              className="Postflop__cbet-inline-header Postflop__cell--muted"
              title={column.headerTitle ?? column.name}
            >
              <span className="Postflop__cbet-inline-label">{label}</span>
              {street && <span className="Postflop__cbet-inline-street">{street}</span>}
            </div>
            <div className="Postflop__cbet-inline-value Postflop__cell--value Postflop__cell--muted">
              <PostflopStatValue value={value} sample={sample} />
            </div>
          </div>
        );
      })}
    </div>
  </div>
);

const StatSectionBlock = ({
  title,
  columns,
  streets,
  mutedColumnNames,
  layout = 'primary',
  display = 'grid',
  frameStyle = 'cbet-block',
  portable,
}: StatSectionBlockConfig & { frameStyle?: 'cbet-block' | 'table-wrap'; portable: boolean }) => {
  if (display === 'inline-row') {
    return <StatInlineRowBlock title={title} columns={columns} portable={portable} />;
  }

  const grid = (
    <PostflopStatsGrid
      columns={columns}
      variant="cbet-block"
      portable={portable}
      wrap={false}
      streets={streets}
      mutedColumnNames={mutedColumnNames}
    />
  );

  if (frameStyle === 'table-wrap') {
    return (
      <div className="Postflop__table-wrap Postflop__table-wrap--preflop-matrix">
        <div className="Postflop__cbet-block-title">{title}</div>
        {grid}
      </div>
    );
  }

  return (
    <div
      className={classNames('Postflop__cbet-block', {
        'Postflop__cbet-block--primary': layout === 'primary',
        'Postflop__cbet-block--side': layout === 'side',
      })}
    >
      <div className="Postflop__cbet-block-title">{title}</div>
      {grid}
    </div>
  );
};

const CbetSection = ({
  mode,
  onModeChange,
  cbetMainColumns,
  cbetLinesColumns,
  fvcbColumns,
  portable,
}: {
  mode: CbetMode;
  onModeChange: (mode: CbetMode) => void;
  cbetMainColumns: StatColumn[];
  cbetLinesColumns: StatColumn[];
  fvcbColumns: StatColumn[];
  portable: boolean;
}) => {
  const cbetBlocks: StatSectionBlockConfig[] = [
    {
      title: 'CBet frequency',
      layout: 'primary',
      mutedColumnNames: ['CBet', 'Cb-fold'],
      columns: [
        ...pickStatColumns(cbetMainColumns, ['CBet']),
        ...pickStatColumns(cbetLinesColumns, ['Cb-fold']),
        ...pickStatColumns(cbetMainColumns, ['CBet IP']),
        ...pickStatColumns(cbetLinesColumns, ['Cb-f IP']),
        ...pickStatColumns(cbetMainColumns, ['CBet OOP']),
        ...pickStatColumns(cbetLinesColumns, ['Cb-f OOP']),
        ...pickStatColumns(cbetMainColumns, ['x_F']),
      ],
    },
    {
      title: 'Lines vs raise',
      layout: 'side',
      display: 'inline-row',
      columns: pickStatColumns(cbetLinesColumns, ['Cb-c&f', 'Cb-c&c\nt&f_r']),
    },
    {
      title: 'River Bx lines',
      layout: 'primary',
      columns: pickStatColumns(cbetMainColumns, ['bxb_IP', 'bxf_IP', 'bxb_OOP', 'bxf_OOP']),
      streets: ['RVR'],
    },
  ];

  const vscbBlocks: StatSectionBlockConfig[] = [
    {
      title: 'vsCb frequency',
      layout: 'primary',
      mutedColumnNames: ['FvCb', 'RCb'],
      columns: [
        ...pickStatColumns(fvcbColumns, ['FvCb']),
        ...pickStatColumns(fvcbColumns, ['RCb']),
        ...pickStatColumns(fvcbColumns, ['FvCB IP']),
        ...pickStatColumns(fvcbColumns, ['RCb IP']),
        ...pickStatColumns(fvcbColumns, ['FvCB OOP']),
        ...pickStatColumns(fvcbColumns, ['RCb OOP']),
      ],
    },
  ];

  return (
    <div className="Postflop__preflop-layout">
      <div className="Postflop__mode-switch" role="tablist" aria-label="CBet mode">
        <button
          type="button"
          role="tab"
          aria-selected={mode === 'cbet'}
          className={classNames('Postflop__mode-switch-btn', {
            'Postflop__mode-switch-btn--active': mode === 'cbet',
          })}
          onClick={() => onModeChange('cbet')}
        >
          CBet
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={mode === 'vscb'}
          className={classNames('Postflop__mode-switch-btn', {
            'Postflop__mode-switch-btn--active': mode === 'vscb',
          })}
          onClick={() => onModeChange('vscb')}
        >
          vsCb
        </button>
      </div>
      {mode === 'cbet' ? (
        <div className="Postflop__cbet-sections">
          <div className="Postflop__cbet-sections-row">
            <StatSectionBlock {...cbetBlocks[0]} frameStyle="table-wrap" portable={portable} />
            <StatSectionBlock {...cbetBlocks[1]} portable={portable} />
          </div>
          <div className="Postflop__cbet-sections-row">
            <StatSectionBlock {...cbetBlocks[2]} frameStyle="table-wrap" portable={portable} />
          </div>
        </div>
      ) : (
        <div className="Postflop__cbet-sections">
          <div className="Postflop__cbet-sections-row">
            <StatSectionBlock {...vscbBlocks[0]} frameStyle="table-wrap" portable={portable} />
          </div>
        </div>
      )}
    </div>
  );
};

const BxSection = ({ bxColumns, portable }: { bxColumns: StatColumn[]; portable: boolean }) => {
  const bxBlocks: StatSectionBlockConfig[] = [
    {
      title: 'River Bx lines',
      layout: 'primary',
      columns: pickStatColumns(bxColumns, [
        'bxb_tot',
        'bxf_tot',
        'bxb_IP',
        'bxf_IP',
        'bxb_OOP',
        'bxf_OOP',
      ]),
      streets: ['RVR'],
    },
    {
      title: 'Lines vs raise',
      layout: 'side',
      display: 'inline-row',
      columns: pickStatColumns(bxColumns, ['B-c&f_t', 'B-c&c_t&f_r']),
    },
    {
      title: 'Bet-fold',
      layout: 'primary',
      columns: pickStatColumns(bxColumns, ['B-f']),
    },
    {
      title: 'RF line',
      layout: 'side',
      display: 'inline-row',
      columns: pickStatColumns(bxColumns, ['RF_af_c_flop_&_tr', 'RF_af_c_tr']),
    },
  ];

  return (
    <div className="Postflop__table-wrap Postflop__table-wrap--cbet">
      <div className="Postflop__cbet-sections Postflop__cbet-sections--standalone">
        <div className="Postflop__cbet-sections-row">
          <StatSectionBlock {...bxBlocks[0]} portable={portable} />
          <StatSectionBlock {...bxBlocks[1]} portable={portable} />
        </div>
        <div className="Postflop__cbet-sections-row">
          <StatSectionBlock {...bxBlocks[2]} portable={portable} />
          <StatSectionBlock {...bxBlocks[3]} portable={portable} />
        </div>
      </div>
    </div>
  );
};

const PreflopMatrixSection = ({
  mode,
  onModeChange,
  preflopOr,
  squeeze1cVs,
  threeBetVs,
  foldFourBetVs,
  fiveBetVs,
  coldCallVs,
}: {
  mode: PreflopMatrixMode;
  onModeChange: (mode: PreflopMatrixMode) => void;
  preflopOr?: PreflopOrMatrix;
  squeeze1cVs?: ThreeBetVsMatrix;
  threeBetVs?: ThreeBetVsMatrix;
  foldFourBetVs?: ThreeBetVsMatrix;
  fiveBetVs?: ThreeBetVsMatrix;
  coldCallVs?: ThreeBetVsMatrix;
}) => (
  <div className="Postflop__preflop-layout">
    <div className="Postflop__mode-switch" role="tablist" aria-label="Preflop mode">
      <button
        type="button"
        role="tab"
        aria-selected={mode === 'or'}
        className={classNames('Postflop__mode-switch-btn', {
          'Postflop__mode-switch-btn--active': mode === 'or',
        })}
        onClick={() => onModeChange('or')}
      >
        Pre TOT
      </button>
      <button
        type="button"
        role="tab"
        aria-selected={mode === 'pre-oop'}
        className={classNames('Postflop__mode-switch-btn', {
          'Postflop__mode-switch-btn--active': mode === 'pre-oop',
        })}
        onClick={() => onModeChange('pre-oop')}
      >
        Pre OOP
      </button>
      <button
        type="button"
        role="tab"
        aria-selected={mode === 'pre-ip'}
        className={classNames('Postflop__mode-switch-btn', {
          'Postflop__mode-switch-btn--active': mode === 'pre-ip',
        })}
        onClick={() => onModeChange('pre-ip')}
      >
        Pre IP
      </button>
      <button
        type="button"
        role="tab"
        aria-selected={mode === '3bet'}
        className={classNames('Postflop__mode-switch-btn', {
          'Postflop__mode-switch-btn--active': mode === '3bet',
        })}
        onClick={() => onModeChange('3bet')}
      >
        3bet
      </button>
      <button
        type="button"
        role="tab"
        aria-selected={mode === 'f4bet'}
        className={classNames('Postflop__mode-switch-btn', {
          'Postflop__mode-switch-btn--active': mode === 'f4bet',
        })}
        onClick={() => onModeChange('f4bet')}
      >
        F4bet
      </button>
      <button
        type="button"
        role="tab"
        aria-selected={mode === '5bet'}
        className={classNames('Postflop__mode-switch-btn', {
          'Postflop__mode-switch-btn--active': mode === '5bet',
        })}
        onClick={() => onModeChange('5bet')}
      >
        5bet
      </button>
      <button
        type="button"
        role="tab"
        aria-selected={mode === 'cc'}
        className={classNames('Postflop__mode-switch-btn', {
          'Postflop__mode-switch-btn--active': mode === 'cc',
        })}
        onClick={() => onModeChange('cc')}
      >
        CC
      </button>
      <button
        type="button"
        role="tab"
        aria-selected={mode === 'sq1c'}
        className={classNames('Postflop__mode-switch-btn', {
          'Postflop__mode-switch-btn--active': mode === 'sq1c',
        })}
        onClick={() => onModeChange('sq1c')}
      >
        Sq_r1+c1
      </button>
    </div>
    {isPreflopOrMatrixMode(mode) ? (
      mode === 'or' ? (
        <PreflopOrMatrixGrid matrix={preflopOr} variant="tot" embedded />
      ) : (
        <div className="Postflop__table-wrap Postflop__table-wrap--preflop-matrix">
          <PreflopOrMatrixGrid
            matrix={preflopOr}
            variant={getPreflopOrGridVariant(mode)}
            embedded
          />
        </div>
      )
    ) : (
      <div className="Postflop__table-wrap Postflop__table-wrap--preflop-matrix">
        <ThreeBetVsMatrixGrid
          embedded
          mode={mode === 'sq1c' ? '3bet' : mode}
          matrix={
            mode === 'sq1c'
              ? squeeze1cVs
              : mode === '3bet'
                ? threeBetVs
                : mode === 'f4bet'
                  ? foldFourBetVs
                  : mode === '5bet'
                    ? fiveBetVs
                    : coldCallVs
          }
        />
      </div>
    )}
  </div>
);

const getCbetBlockGridClass = (columnCount: number) => {
  const clamped = Math.min(Math.max(columnCount, 1), 9);
  return `Postflop__grid--cbet-block-${clamped}`;
};

const PostflopStatsGrid = ({
  columns,
  donkColumn,
  xrColumn,
  agfColumn,
  variant = 'main',
  splitAfterIndex,
  splitAfterIndices,
  portable = false,
  wrap = true,
  streets,
  mutedColumnNames,
}: {
  columns: StatColumn[];
  donkColumn?: StatColumn;
  xrColumn?: StatColumn;
  agfColumn?: StatColumn;
  variant?: 'main' | 'donk' | 'fvcb' | 'cbet' | 'cbet-block' | 'cbet-lines' | 'bx';
  splitAfterIndex?: number;
  splitAfterIndices?: number[];
  portable?: boolean;
  wrap?: boolean;
  streets?: readonly (typeof STREETS)[number][];
  mutedColumnNames?: string[];
}) => {
  const activeStreets = streets ?? STREETS;
  const mutedColumns = React.useMemo(() => new Set(mutedColumnNames ?? []), [mutedColumnNames]);
  const isMutedColumn = (name: string) => mutedColumns.has(name);
  const columnGroups = React.useMemo(() => {
    const splitPoints =
      splitAfterIndices ?? (splitAfterIndex !== undefined ? [splitAfterIndex] : undefined);

    if (!splitPoints?.length) {
      return [columns];
    }

    const groups: StatColumn[][] = [];
    let start = 0;

    splitPoints.forEach((point) => {
      groups.push(columns.slice(start, point + 1));
      start = point + 1;
    });
    groups.push(columns.slice(start));

    return groups.filter((group) => group.length > 0);
  }, [columns, splitAfterIndex, splitAfterIndices]);

  const getGridClassSuffix = (groupIndex: number, colCount: number) => {
    if (columnGroups.length === 1) {
      return '';
    }

    if (variant === 'cbet-lines') {
      if (groupIndex === 0) {
        return '-left';
      }

      if (groupIndex === columnGroups.length - 1) {
        return '-right';
      }

      return '-mid';
    }

    if (groupIndex === 0) {
      return '-left';
    }

    if (groupIndex === columnGroups.length - 1) {
      if (variant === 'bx' && colCount >= 4) {
        return `-lines${colCount}`;
      }

      return '-right';
    }

    if (variant === 'cbet' && colCount === 4) {
      return '-mid2';
    }

    return '-mid';
  };

  const renderGrid = (
    cols: StatColumn[],
    gridClass: string,
    withStreets: boolean,
    streetRows: readonly (typeof STREETS)[number][] = activeStreets,
  ) => (
    <div
      className={`Postflop__grid ${gridClass}`}
      style={
        variant === 'cbet-block'
          ? { gridTemplateRows: `auto repeat(${streetRows.length}, ${portable ? 34 : 36}px)` }
          : undefined
      }
    >
      {withStreets && <div className="Postflop__cell Postflop__cell--corner" />}
      {cols.map((col) => (
        <div
          key={col.name}
          className={classNames('Postflop__cell', 'Postflop__cell--header', {
            'Postflop__cell--muted': isMutedColumn(col.name),
          })}
          title={col.headerTitle ?? col.name}
        >
          {portable && col.portableName ? col.portableName : col.name}
        </div>
      ))}
      {streetRows.map((street) => {
        const rowIndex = STREETS.indexOf(street);

        return (
          <React.Fragment key={street}>
            {withStreets && <div className="Postflop__cell Postflop__cell--street">{street}</div>}
            {cols.map((col) => {
              const sample = col.sampleValues?.[rowIndex];
              const value = col.values[rowIndex];

              return (
                <div
                  key={`${street}-${col.name}`}
                  className={classNames('Postflop__cell', 'Postflop__cell--value', {
                    'Postflop__cell--muted': isMutedColumn(col.name),
                  })}
                >
                  <PostflopStatValue value={value} sample={sample} />
                </div>
              );
            })}
          </React.Fragment>
        );
      })}
    </div>
  );

  const renderSideColumn = (col: StatColumn, extraClass?: string) => (
    <div className={classNames('Postflop__grid', 'Postflop__grid--side', extraClass)}>
      <div className="Postflop__cell Postflop__cell--header" title={col.headerTitle ?? col.name}>
        {portable && col.portableName ? col.portableName : col.name}
      </div>
      {col.values.map((value, index) => {
        const sample = col.sampleValues?.[index];

        return (
          <div key={index} className="Postflop__cell Postflop__cell--value">
            <PostflopStatValue value={value} sample={sample} />
          </div>
        );
      })}
    </div>
  );

  const fillTable = portable && variant === 'main';
  const wideTable = variant === 'bx';

  const table = (
    <div
      className={classNames('Postflop__table', {
        'Postflop__table--fill': fillTable,
        'Postflop__table--cbet-lines': variant === 'cbet-lines',
      })}
    >
      {columnGroups.length > 1
        ? columnGroups.map((group, groupIndex) => (
            <React.Fragment key={groupIndex}>
              {groupIndex > 0 && <div className="Postflop__divider" />}
              {renderGrid(
                group,
                `Postflop__grid--${variant}${getGridClassSuffix(groupIndex, group.length)}`,
                groupIndex === 0,
              )}
            </React.Fragment>
          ))
        : renderGrid(
            columnGroups[0],
            variant === 'cbet-block'
              ? getCbetBlockGridClass(columnGroups[0].length)
              : `Postflop__grid--${variant}`,
            true,
          )}

      {donkColumn && (
        <>
          <div className="Postflop__divider" />
          {renderSideColumn(donkColumn)}
        </>
      )}

      {xrColumn && (
        <>
          <div className="Postflop__divider" />
          {renderSideColumn(xrColumn)}
        </>
      )}

      {agfColumn && (
        <>
          <div className="Postflop__divider" />
          {renderSideColumn(agfColumn, portable ? 'Postflop__grid--side-agf' : undefined)}
        </>
      )}
    </div>
  );

  if (!wrap) {
    return table;
  }

  return (
    <div
      className={classNames('Postflop__table-wrap', {
        'Postflop__table-wrap--fill': fillTable,
        'Postflop__table-wrap--wide': wideTable,
      })}
    >
      {table}
    </div>
  );
};

type PostflopStatsProps = {
  portable?: boolean;
  embedded?: boolean;
  forcedView?: PostflopView;
};

const PostflopStats = React.memo(
  ({ portable = false, embedded = false, forcedView }: PostflopStatsProps) => {
    const generalStats = useSelector(selectGeneralStats);
    const [view, setView] = useState<PostflopView>(forcedView ?? 'main');
    const [preflopMode, setPreflopMode] = useState<PreflopMatrixMode>('or');
    const [cbetMode, setCbetMode] = useState<CbetMode>('cbet');

    React.useEffect(() => {
      if (forcedView) {
        setView(forcedView);
      }
    }, [forcedView]);

    const activeView = forcedView ?? view;

    const mainColumns: StatColumn[] = [
      {
        name: 'CBet',
        values: [
          generalStats.cbetflop ?? null,
          generalStats.cbetturn ?? null,
          generalStats.cbetriver ?? null,
        ],
        sampleValues: [
          generalStats.cbetfloppossible ?? null,
          generalStats.cbetturnpossible ?? null,
          generalStats.cbetriverpossible ?? null,
        ],
      },
      {
        name: 'FvCb',
        values: [
          generalStats.foldflopcbet ?? null,
          generalStats.foldturncbet ?? null,
          generalStats.foldrivercbet ?? null,
        ],
        sampleValues: [
          generalStats.facingflopcbet ?? null,
          generalStats.facingturncbet ?? null,
          generalStats.facingrivercbet ?? null,
        ],
      },
      {
        name: 'RCb',
        values: [
          generalStats.raisedflopcontinuationbet ?? null,
          generalStats.raisedturncontinuationbet ?? null,
          generalStats.raisedrivercontinuationbet ?? null,
        ],
        sampleValues: [
          generalStats.facingflopcbet ?? null,
          generalStats.facingturncbet ?? null,
          generalStats.facingrivercbet ?? null,
        ],
      },
      {
        name: 'Bet',
        values: [
          generalStats.flopbet ?? null,
          generalStats.turnbet ?? null,
          generalStats.riverbet ?? null,
        ],
        sampleValues: [
          generalStats.flopbetpossible ?? null,
          generalStats.turnbetpossible ?? null,
          generalStats.riverbetpossible ?? null,
        ],
      },
      {
        name: 'B-F',
        values: [
          generalStats.betfoldflop ?? null,
          generalStats.betfoldturn ?? null,
          generalStats.betfoldriver ?? null,
        ],
        sampleValues: [
          generalStats.betfoldfloppossible ?? null,
          generalStats.betfoldturnpossible ?? null,
          generalStats.betfoldriverpossible ?? null,
        ],
      },
      {
        name: 'FvBet',
        values: [
          generalStats.flopfold ?? null,
          generalStats.turnfold ?? null,
          generalStats.riverfold ?? null,
        ],
        sampleValues: [
          generalStats.flopfoldpossible ?? null,
          generalStats.turnfoldpossible ?? null,
          generalStats.riverfoldpossible ?? null,
        ],
      },
      {
        name: 'RBet',
        values: [
          generalStats.flopraise ?? null,
          generalStats.turnraise ?? null,
          generalStats.riverraise ?? null,
        ],
        sampleValues: [
          generalStats.flopraisepossible ?? null,
          generalStats.turnraisepossible ?? null,
          generalStats.riverraisepossible ?? null,
        ],
      },
      {
        name: 'W_after_r',
        values: [
          generalStats.flopwafterr ?? null,
          generalStats.turnwafterr ?? null,
          generalStats.riverwafterr ?? null,
        ],
        sampleValues: [
          generalStats.flopwafterrpossible ?? null,
          generalStats.turnwafterrpossible ?? null,
          generalStats.riverwafterrpossible ?? null,
        ],
      },
    ];

    const fvcbColumns: StatColumn[] = [
      {
        name: 'FvCb',
        values: [
          generalStats.foldflopcbet ?? null,
          generalStats.foldturncbet ?? null,
          generalStats.foldrivercbet ?? null,
        ],
        sampleValues: [
          generalStats.facingflopcbet ?? null,
          generalStats.facingturncbet ?? null,
          generalStats.facingrivercbet ?? null,
        ],
      },
      {
        name: 'FvCB IP',
        values: [
          generalStats.foldflopcbetip ?? null,
          generalStats.foldturncbetip ?? null,
          generalStats.foldrivercbetip ?? null,
        ],
        sampleValues: [
          generalStats.foldflopcbetippossible ?? null,
          generalStats.foldturncbetippossible ?? null,
          generalStats.foldrivercbetippossible ?? null,
        ],
      },
      {
        name: 'FvCB OOP',
        values: [
          generalStats.foldflopcbetoop ?? null,
          generalStats.foldturncbetoop ?? null,
          generalStats.foldrivercbetoop ?? null,
        ],
        sampleValues: [
          generalStats.foldflopcbetooppossible ?? null,
          generalStats.foldturncbetooppossible ?? null,
          generalStats.foldrivercbetooppossible ?? null,
        ],
      },
      {
        name: 'RCb',
        values: [
          generalStats.raisedflopcontinuationbet ?? null,
          generalStats.raisedturncontinuationbet ?? null,
          generalStats.raisedrivercontinuationbet ?? null,
        ],
        sampleValues: [
          generalStats.facingflopcbet ?? null,
          generalStats.facingturncbet ?? null,
          generalStats.facingrivercbet ?? null,
        ],
      },
      {
        name: 'RCb IP',
        portableName: 'RCb\nIP',
        values: [
          generalStats.raisedflopcontinuationbetip ?? null,
          generalStats.raisedturncontinuationbetip ?? null,
          generalStats.raisedrivercontinuationbetip ?? null,
        ],
        sampleValues: [
          generalStats.raisedflopcontinuationbetippossible ?? null,
          generalStats.raisedturncontinuationbetippossible ?? null,
          generalStats.raisedrivercontinuationbetippossible ?? null,
        ],
      },
      {
        name: 'RCb OOP',
        portableName: 'RCb\nOOP',
        values: [
          generalStats.raisedflopcontinuationbetoop ?? null,
          generalStats.raisedturncontinuationbetoop ?? null,
          generalStats.raisedrivercontinuationbetoop ?? null,
        ],
        sampleValues: [
          generalStats.raisedflopcontinuationbetooppossible ?? null,
          generalStats.raisedturncontinuationbetooppossible ?? null,
          generalStats.raisedrivercontinuationbetooppossible ?? null,
        ],
      },
    ];

    const cbetMainColumns: StatColumn[] = [
      {
        name: 'CBet',
        values: [
          generalStats.cbetflop ?? null,
          generalStats.cbetturn ?? null,
          generalStats.cbetriver ?? null,
        ],
        sampleValues: [
          generalStats.cbetfloppossible ?? null,
          generalStats.cbetturnpossible ?? null,
          generalStats.cbetriverpossible ?? null,
        ],
      },
      {
        name: 'CBet IP',
        portableName: 'CBet\nIP',
        values: [
          generalStats.cbetflopip ?? null,
          generalStats.cbetturnip ?? null,
          generalStats.cbetriverip ?? null,
        ],
        sampleValues: [
          generalStats.cbetflopippossible ?? null,
          generalStats.cbetturnippossible ?? null,
          generalStats.cbetriverippossible ?? null,
        ],
      },
      {
        name: 'bxb_IP',
        portableName: 'bxb\nIP',
        values: [null, null, generalStats.bxbip ?? null],
        sampleValues: [null, null, generalStats.bxbippossible ?? null],
      },
      {
        name: 'bxf_IP',
        portableName: 'bxf\nIP',
        values: [null, null, generalStats.bxfip ?? null],
        sampleValues: [null, null, generalStats.bxfippossible ?? null],
      },
      {
        name: 'CBet OOP',
        portableName: 'CBet\nOOP',
        values: [
          generalStats.cbetflopoop ?? null,
          generalStats.cbetturnoop ?? null,
          generalStats.cbetriveroop ?? null,
        ],
        sampleValues: [
          generalStats.cbetflopooppossible ?? null,
          generalStats.cbetturnooppossible ?? null,
          generalStats.cbetriverooppossible ?? null,
        ],
      },
      {
        name: 'bxb_OOP',
        portableName: 'bxb\nOOP',
        values: [null, null, generalStats.bxboop ?? null],
        sampleValues: [null, null, generalStats.bxbooppossible ?? null],
      },
      {
        name: 'bxf_OOP',
        portableName: 'bxf\nOOP',
        values: [null, null, generalStats.bxfoop ?? null],
        sampleValues: [null, null, generalStats.bxfooppossible ?? null],
      },
      {
        name: 'x_F',
        values: [
          generalStats.x_fflop ?? null,
          generalStats.x_ffturn ?? null,
          generalStats.x_ffriver ?? null,
        ],
        sampleValues: [
          generalStats.x_ffloppossible ?? null,
          generalStats.x_ffturnpossible ?? null,
          generalStats.x_ffriverpossible ?? null,
        ],
      },
    ];

    const cbetLinesColumns: StatColumn[] = [
      {
        name: 'Cb-fold',
        portableName: 'Cb\nfold',
        values: [generalStats.cbfoldraise ?? null, null, null],
        sampleValues: [generalStats.cbfoldraisepossible ?? null, null, null],
      },
      {
        name: 'Cb-f IP',
        portableName: 'Cb-f\nIP',
        values: [generalStats.cbfoldraiseip ?? null, null, null],
        sampleValues: [generalStats.cbfoldraiseippossible ?? null, null, null],
      },
      {
        name: 'Cb-f OOP',
        portableName: 'Cb-f\nOOP',
        values: [generalStats.cbfoldraiseoop ?? null, null, null],
        sampleValues: [generalStats.cbfoldraiseooppossible ?? null, null, null],
      },
      {
        name: 'Cb-c&f',
        portableName: 'Cb-c&f',
        values: [null, generalStats.cbcallfoldturn ?? null, null],
        sampleValues: [null, generalStats.cbcallfoldturnpossible ?? null, null],
      },
      {
        name: 'Cb-c&c\nt&f_r',
        portableName: 'Cb-c&c\nt&f_r',
        headerTitle: 'Cb-c&c_t&f_r',
        values: [null, null, generalStats.cbcallcallfoldriver ?? null],
        sampleValues: [null, null, generalStats.cbcallcallfoldriverpossible ?? null],
      },
    ];

    const bxColumns: StatColumn[] = [
      {
        name: 'bxb_tot',
        portableName: 'bxb\ntot',
        values: [null, null, generalStats.bxbtot ?? null],
        sampleValues: [null, null, generalStats.bxbtotpossible ?? null],
      },
      {
        name: 'bxf_tot',
        portableName: 'bxf\ntot',
        values: [null, null, generalStats.bxftot ?? null],
        sampleValues: [null, null, generalStats.bxftotpossible ?? null],
      },
      {
        name: 'bxb_IP',
        portableName: 'bxb\nIP',
        values: [null, null, generalStats.bxbip ?? null],
        sampleValues: [null, null, generalStats.bxbippossible ?? null],
      },
      {
        name: 'bxf_IP',
        portableName: 'bxf\nIP',
        values: [null, null, generalStats.bxfip ?? null],
        sampleValues: [null, null, generalStats.bxfippossible ?? null],
      },
      {
        name: 'bxb_OOP',
        portableName: 'bxb\nOOP',
        values: [null, null, generalStats.bxboop ?? null],
        sampleValues: [null, null, generalStats.bxbooppossible ?? null],
      },
      {
        name: 'bxf_OOP',
        portableName: 'bxf\nOOP',
        values: [null, null, generalStats.bxfoop ?? null],
        sampleValues: [null, null, generalStats.bxfooppossible ?? null],
      },
      {
        name: 'B-f',
        values: [
          generalStats.betfoldflop ?? null,
          generalStats.betfoldturn ?? null,
          generalStats.betfoldriver ?? null,
        ],
        sampleValues: [
          generalStats.betfoldfloppossible ?? null,
          generalStats.betfoldturnpossible ?? null,
          generalStats.betfoldriverpossible ?? null,
        ],
      },
      {
        name: 'B-c&f_t',
        portableName: 'B-c&f\nt',
        values: [null, generalStats.bcallfoldturn ?? null, null],
        sampleValues: [null, generalStats.bcallfoldturnpossible ?? null, null],
      },
      {
        name: 'B-c&c_t&f_r',
        portableName: 'B-c&c\nt&f_r',
        values: [null, null, generalStats.bcallcallfoldriver ?? null],
        sampleValues: [null, null, generalStats.bcallcallfoldriverpossible ?? null],
      },
      {
        name: 'RF_af_c_flop_&_tr',
        portableName: 'RF_af\n_c_flop\n&_tr',
        values: [null, null, generalStats.rfaftercallflopturnfold ?? null],
        sampleValues: [null, null, generalStats.rfaftercallflopturnfoldpossible ?? null],
      },
      {
        name: 'RF_af_c_tr',
        portableName: 'RF_af\n_c_tr',
        values: [null, null, generalStats.rfaftercallturnfold ?? null],
        sampleValues: [null, null, generalStats.rfaftercallturnfoldpossible ?? null],
      },
    ];

    const donkColumns: StatColumn[] = [
      {
        name: 'Donk',
        values: [
          generalStats.donkflop ?? null,
          generalStats.donkturn ?? null,
          generalStats.donkriver ?? null,
        ],
        sampleValues: [
          generalStats.donkfloppossible ?? null,
          generalStats.donkturnpossible ?? null,
          generalStats.donkriverpossible ?? null,
        ],
      },
      {
        name: 'D_3bp',
        values: [
          generalStats.donk3betpflop ?? null,
          generalStats.donk3betpturn ?? null,
          generalStats.donk3betpriver ?? null,
        ],
        sampleValues: [
          generalStats.donk3betpfloppossible ?? null,
          generalStats.donk3betpturnpossible ?? null,
          generalStats.donk3betpriverpossible ?? null,
        ],
      },
      {
        name: 'FvD',
        values: [
          generalStats.foldvsdonkflop ?? null,
          generalStats.foldvsdonkturn ?? null,
          generalStats.foldvsdonkriver ?? null,
        ],
        sampleValues: [
          generalStats.facingflopdonk ?? null,
          generalStats.facingturndonk ?? null,
          generalStats.facingriverdonk ?? null,
        ],
      },
      {
        name: 'D-fold',
        values: [
          generalStats.donkfoldflop ?? null,
          generalStats.donkfoldturn ?? null,
          generalStats.donkfoldriver ?? null,
        ],
        sampleValues: [
          generalStats.donkfoldfloppossible ?? null,
          generalStats.donkfoldturnpossible ?? null,
          generalStats.donkfoldriverpossible ?? null,
        ],
      },
    ];

    const donkColumn: StatColumn = {
      name: 'Donk',
      values: [
        generalStats.donkflop ?? null,
        generalStats.donkturn ?? null,
        generalStats.donkriver ?? null,
      ],
      sampleValues: [
        generalStats.donkfloppossible ?? null,
        generalStats.donkturnpossible ?? null,
        generalStats.donkriverpossible ?? null,
      ],
    };

    const xrColumn: StatColumn = {
      name: 'xR',
      values: [
        generalStats.xrflop ?? null,
        generalStats.xrturn ?? null,
        generalStats.xrriver ?? null,
      ],
      sampleValues: [
        generalStats.xrfloppossible ?? null,
        generalStats.xrturnpossible ?? null,
        generalStats.xrriverpossible ?? null,
      ],
    };

    const agfColumn: StatColumn = {
      name: portable ? 'Agf' : 'Agf %',
      headerTitle: 'Agf %',
      values: [
        generalStats.flopagrfactor ?? null,
        generalStats.turnagrfactor ?? null,
        generalStats.riveragrfactor ?? null,
      ],
      sampleValues: [
        generalStats.flopagfpossible ?? null,
        generalStats.turnagfpossible ?? null,
        generalStats.riveragfpossible ?? null,
      ],
    };

    const statTabs = (
      <>
        <button
          type="button"
          className={classNames('Postflop__tab', {
            'Postflop__tab--active': view === 'main',
          })}
          onClick={() => setView('main')}
        >
          General
        </button>
        <button
          type="button"
          className={classNames('Postflop__tab', {
            'Postflop__tab--active': view === 'preflop',
          })}
          onClick={() => setView('preflop')}
        >
          Preflop
        </button>
        <button
          type="button"
          className={classNames('Postflop__tab', {
            'Postflop__tab--active': view === 'donk',
          })}
          onClick={() => setView('donk')}
        >
          Donk
        </button>
        <button
          type="button"
          className={classNames('Postflop__tab', {
            'Postflop__tab--active': view === 'cbet',
          })}
          onClick={() => setView('cbet')}
        >
          CBet
        </button>
        <button
          type="button"
          className={classNames('Postflop__tab', {
            'Postflop__tab--active': view === 'bx',
          })}
          onClick={() => setView('bx')}
        >
          B/x
        </button>
      </>
    );

    const tabs = <div className="Postflop__tabs">{statTabs}</div>;

    const grid =
      activeView === 'main' ? (
        <PostflopStatsGrid
          columns={mainColumns}
          donkColumn={donkColumn}
          xrColumn={xrColumn}
          agfColumn={agfColumn}
          variant="main"
          splitAfterIndex={2}
          portable={portable}
          mutedColumnNames={['B-F']}
        />
      ) : activeView === 'donk' ? (
        <PostflopStatsGrid columns={donkColumns} variant="donk" />
      ) : activeView === 'cbet' ? (
        <CbetSection
          mode={cbetMode}
          onModeChange={setCbetMode}
          cbetMainColumns={cbetMainColumns}
          cbetLinesColumns={cbetLinesColumns}
          fvcbColumns={fvcbColumns}
          portable={portable}
        />
      ) : activeView === 'preflop' ? (
        <PreflopMatrixSection
          mode={preflopMode}
          onModeChange={setPreflopMode}
          preflopOr={generalStats.preflopOr}
          squeeze1cVs={generalStats.squeeze1cVs}
          threeBetVs={generalStats.threeBetVs}
          foldFourBetVs={generalStats.foldFourBetVs}
          fiveBetVs={generalStats.fiveBetVs}
          coldCallVs={generalStats.coldCallVs}
        />
      ) : (
        <BxSection bxColumns={bxColumns} portable={portable} />
      );

    if (embedded) {
      return (
        <div className="Postflop Postflop--embedded">
          <div className="Postflop__content Postflop__content--embedded">{grid}</div>
        </div>
      );
    }

    if (portable) {
      return (
        <section className="Postflop Postflop--portable">
          <div className="Postflop--portable__head">
            <Text bold size={2} type="primary">
              Postflop
            </Text>
            {tabs}
          </div>
          <div className="Postflop__content Postflop__content--portable">{grid}</div>
        </section>
      );
    }

    return (
      <Card title="Postflop">
        <div className="Postflop__content">
          {tabs}
          {grid}
        </div>
      </Card>
    );
  },
);

export default PostflopStats;
